<template>
  <div class="w-full h-chart flex justify-center relative">
    <div class="w-chart h-full" ref="charts"></div>
    <div
      v-if="source.length === 0"
      class="w-full h-full absolute left-0 top-0 z-10 flex flex-col items-center justify-center bg-white"
    >
      <el-empty :image-size="260" description="暂无图例" />
    </div>
  </div>
</template>

<script>
import echarts from 'echarts/lib/echarts'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/dataZoom'
import 'echarts/lib/chart/radar'
import 'echarts/lib/chart/bar'
import 'echarts/lib/chart/line'
import { dataURLToBlob } from '@dygc/shared'
import { chartTypeCode } from '@/global/analysis'

export default {
  name: 'AnalysisChart',
  props: {
    type: {
      type: String,
      default: ''
    },
    source: {
      type: Array,
      default: () => []
    },
    mergeLegend: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      LEGEND: this.getLegend(),
      COLOR: this.getColor(),
      chart: null,
      ITEM_SIZE: 120,
    }
  },
  watch: {
    type() {
      this.setChart()
    },
    source: {
      handler() {
        this.setChart()
      },
      deep: true
    },
    mergeLegend: {
      handler() {
        this.LEGEND = this.getLegend();
        this.COLOR = this.getColor();
        this.setChart()
      },
      deep: true,
    }
  },
  mounted() {
    this.initChart()
    this.setChart()
  },
  beforeDestroy() {
    this.destroyChart()
  },
  methods: {
    initChart() {
      if (this.$refs.charts) {
        this.chart = echarts.init(this.$refs.charts)
      }
    },
    destroyChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },
    getLegend() {
      const [mlg1 = {}, mlg2 = {}] = this.mergeLegend || [];
      const legendList = [];
      if (mlg1?.show !== false) {
        legendList.push(mlg1?.legend ? mlg1.legend : '标准分值');
      }
      if (mlg2?.show !== false) {
        legendList.push(mlg2?.legend ? mlg2.legend : '平均分值');
      }
      return legendList;
    },
    getColor() {
      const [mlg1 = {}, mlg2 = {}] = this.mergeLegend || [];
      const colorList = [];
      if (mlg1?.show !== false) {
        colorList.push('#2775E8');
      }
      if (mlg2?.show !== false) {
        colorList.push('#00BE6A');
      }
      return colorList;
    },
    getSeries({ averageList, standardList }) {
      const [mlg1 = {}, mlg2 = {}] = this.mergeLegend || [];
      const list = [];
      if (mlg1?.show !== false) {
        list.push(standardList);
      }
      if (mlg2?.show !== false) {
        list.push(averageList);
      }
      const radar = [
        {
          name: '数据',
          type: 'radar',
          data: []
        }
      ]
      const bar = []
      const line = []
      list.forEach((arr, i) => {
        const hexColor = this.COLOR[i].replace('#', '')
        const R = `${hexColor[0]}${hexColor[1]}`
        const G = `${hexColor[2]}${hexColor[3]}`
        const B = `${hexColor[4]}${hexColor[5]}`
        const areaColor = `rgba(${parseInt(R, 16)},${parseInt(G, 16)},${parseInt(B, 16)}, 0.5)`
        radar[0].data.push({
          name: this.LEGEND[i],
          value: arr,
          areaStyle: {
            color: areaColor
          }
        })
        bar.push({
          type: 'bar',
          barGap: 0,
          barWidth: 30,
          barMinWidth: 15,
          barMaxWidth: 40,
          name: this.LEGEND[i],
          data: arr
        })
        line.push({
          type: 'line',
          name: this.LEGEND[i],
          data: arr
        })
      })
      return { radar, bar, line }
    },
    getSource() {
      const maxList = []
      const indicatorList = []
      const averageList = []
      const standardList = []
      for (const se of this.source) {
        const { average = 0, standard = 0 } = se
        const ms = Math.max(average, standard)
        maxList.push(ms)
        averageList.push(average)
        standardList.push(standard)
      }
      const mScore = maxList.length > 0 ? Math.max(...maxList) : 0
      const maxScore = mScore === 0 ? 1 : mScore;
      const max = Math.ceil(maxScore / 5) * 5
      for (const se of this.source) {
        const { title = '' } = se;
        indicatorList.push({ name: title, max })
      }
      if (indicatorList[0]) {
        indicatorList[0].axisLabel = { show: true }
      }
      return { indicatorList, averageList, standardList }
    },
    getOptions() {
      const { indicatorList, averageList, standardList } = this.getSource()
      const { radar, bar, line } = this.getSeries({ averageList, standardList })
      const titleList = indicatorList.map(m => m.name)
      const dataZoom = this.getSliderSetting();
      const sliderSetting = dataZoom === null ? {} : { dataZoom };
      switch (this.type) {
        case chartTypeCode.LEIDA:
          return {
            color: this.COLOR,
            tooltip: { show: true },
            legend: {
              x: 'right',
              data: this.LEGEND
            },
            radar: {
              indicator: indicatorList,
              splitArea: {
                areaStyle: {
                  color: new Array(5).fill('#fff')
                }
              }
            },
            series: radar
          }
        case chartTypeCode.ZHUZHUANG:
          return {
            color: this.COLOR,
            tooltip: { show: true },
            grid: {
              left: '15%',
              right: '5%',
              bottom: dataZoom === null ? 15 : 85
            },
            legend: {
              x: 'right',
              data: this.LEGEND
            },
            xAxis: {
              type: 'category',
              data: titleList,
              axisLabel: {
                interval: 0,
                formatter: this.axisLabelFormatter
              }
            },
            yAxis: {
              type: 'value'
            },
            series: bar,
            ...sliderSetting
          }
        case chartTypeCode.ZHEXIAN:
          return {
            color: this.COLOR,
            tooltip: { show: true },
            grid: {
              left: '15%',
              right: '5%',
              bottom: dataZoom === null ? 15 : 85
            },
            legend: {
              x: 'right',
              data: this.LEGEND
            },
            xAxis: {
              type: 'category',
              data: titleList,
              axisLabel: {
                interval: 0,
                formatter: this.axisLabelFormatter
              }
            },
            yAxis: {
              type: 'value'
            },
            series: line,
            ...sliderSetting
          }
        default:
          return {}
      }
    },
    axisLabelFormatter(str) {
      let result = '';
      for (let i = 0; i < str.length; i++) {
        result += str[i];
        // 6个字符一行
        if (i > 0 && i % 6 === 0) {
          result += '\n';
        }
      }
      return result;
    },
    getSliderSetting() {
      let end = null;
      if (this.$refs.charts) {
        const { width } = this.$refs.charts.getBoundingClientRect();
        const scale = width / (this.ITEM_SIZE * this.source.length);
        if (scale < 1) {
          end = Math.round(scale * 100);
        }
      }
      if (end === null) return null;
      return {
        type: "slider",
        showDetail: false,
        borderColor: "transparent",
        backgroundColor: "rgba(244,244,244, 0.7)",
        fillerColor: "#d8d8d8",
        dataBackground: {
          lineStyle: {
            color: "transparent",
            with: 0
          },
          areaStyle: {
            color: "transparent",
            with: 0
          }
        },
        filterMode: "empty",
        handleSize: 0,
        height: 12,
        bottom: 0,
        start: 0,
        end
      }
    },
    setChart() {
      if (this.chart) {
        const option = this.getOptions()
        this.chart.clear()
        if (this.source.length > 0) {
          this.chart.setOption(option)
        }
      }
    },
    getImage(fileType) {
      if (this.chart) {
        const dataURL = this.chart.getDataURL({
          type: 'png',
          excludeComponents: ['toolbox']
        })
        if (fileType === 'blob') {
          return dataURLToBlob(dataURL)
        }
        return dataURL
      }
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.w-chart {
  width: 600px;
}
.h-chart {
  height: 385px;
}
</style>
